import FabricAnalyticsListeners, { FabricChannel } from '@atlaskit/analytics-listeners';
import ArrowLeft from '@atlaskit/icon/glyph/arrow-left';
import { B50, N30A } from '@atlaskit/theme/colors';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import styled from 'styled-components';

import { AnalyticsConsumerProps, withAnalytics } from '../hoc/withAnalytics';
import { getFabricAnalyticsListenerAdapter } from '../hooks/useFabricAnalyticsListener';
import Store from '../store';
import DrawerStore, { Drawer as DrawerType } from '../store/drawerStore';

import { onDesktop, onMobile } from './mobile/responsive';
import Switcher from './Switcher';

interface DrawerProps {
  isOpen: boolean;
  size?: string;
}

const whenOpen = (rule: string) => {
  return ({ isOpen }: DrawerProps) => {
    return isOpen ? rule : '';
  };
};

const whenClosed = (rule: string) => {
  return ({ isOpen }: DrawerProps) => {
    return isOpen ? '' : rule;
  };
};

const drawerTransitionSeconds = 0.25;

const Background = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  transition: background ${drawerTransitionSeconds}s;
  will-change: background;

  ${onDesktop(`
    // Ensure side nav is HIDDEN when drawer is opened
    z-index: 5;
  `)}

  ${onMobile(`
    // Ensure top nav is SHOWN when drawer is opened
    z-index: 2;
  `)}

  ${whenOpen(`
    background: rgba(50, 50, 50, 0.5);
  `)};
  ${whenClosed(`
    pointer-events: none;
  `)};
`;

const DrawerContainer = styled.div`
  position: fixed;
  pointer-events: all;
  top: 0;
  left: 0;
  width: ${(props: DrawerProps) => (props.size === 'small' ? '360px' : '500px')};
  height: 100vh;
  transform: translateX(-100%);
  will-change: transform;
  transition: transform ${drawerTransitionSeconds}s;
  background: white;
  display: flex;
  align-items: stretch;
  justify-content: center;

  // Spacing for desktop. To emulate the real AtlasKit drawer
  ${onDesktop(`
    padding-top: 24px;
  `)}

  // Spacing for mobile. Use the full width because mobile doesn't have enough screen estate
  ${onMobile(`
    padding-top: 60px;
    padding-left: 10px;
    width: 100%;
  `)}

  ${whenOpen(`
    transform: translateX(0);
  `)};
`;

const SwitcherContainer = styled.div`
  width: 100%;
  overflow: auto;
`;

const SwitcherDrawerIconWrapper = styled.button`
  &:active {
    background-color: ${B50};
    outline: 0;
  }
  &:hover {
    background-color: ${N30A};
  }
  align-items: center;
  background: 0;
  border: 0;
  border-radius: 50%;
  color: inherit;
  cursor: pointer;
  display: flex;
  font-size: inherit;
  height: 40px;
  justify-content: center;
  lineheight: 1;
  margin-bottom: 16px;
  padding: 0;
  width: 40px;
`;

const SwitcherDrawerContainer = styled.div`
  display: flex;
  width: 100%;
`;

const SwitcherDrawerSidebar = styled.div`
  ${onDesktop(`
    width: 64px;
    display: flex;
    flex-shrink: 0;
    justify-content: center;
  `)}
  ${onMobile(`
    display: none;
  `)}
`;

interface Props {
  drawerStore: DrawerStore;
}

interface State {
  // The currently closing drawer
  closingDrawer: DrawerType;
}

@observer
class Drawer extends React.Component<Props & InjectedIntlProps & AnalyticsConsumerProps, State> {
  state = {
    closingDrawer: DrawerType.None,
  };

  private pruneDrawerTimeout?: number;

  componentDidUpdate(prevProps: Props, prevState: State) {
    // Once the drawer has transitioned away, remove it from the DOM.
    if (prevState.closingDrawer === DrawerType.None && this.state.closingDrawer !== DrawerType.None) {
      this.pruneDrawerTimeout = window.setTimeout(this.pruneDrawer, drawerTransitionSeconds * 1000);
    }
  }

  componentWillUnmount() {
    if (this.pruneDrawerTimeout) {
      clearTimeout(this.pruneDrawerTimeout);
      this.pruneDrawerTimeout = undefined;
    }
  }

  render() {
    const { currentDrawer } = this.props.drawerStore;
    const { closingDrawer } = this.state;

    const renderSwitcherContent = currentDrawer === DrawerType.Switcher || closingDrawer === DrawerType.Switcher;

    const isDrawerOpen = currentDrawer === DrawerType.Switcher;
    const size = 'small';

    return (
      <Background
        data-test-id="drawer-background"
        isOpen={currentDrawer !== DrawerType.None}
        onClick={this.onBackgroundClick}
      >
        <DrawerContainer isOpen={isDrawerOpen} size={size}>
          {renderSwitcherContent && (
            <SwitcherDrawerContainer>
              <SwitcherDrawerSidebar>
                <SwitcherDrawerIconWrapper onClick={this.closeDrawer}>
                  <ArrowLeft label="Close drawer" />
                </SwitcherDrawerIconWrapper>
              </SwitcherDrawerSidebar>
              <SwitcherContainer>
                <FabricAnalyticsListeners
                  client={getFabricAnalyticsListenerAdapter(this.props.analytics)}
                  excludedChannels={[
                    FabricChannel.atlaskit,
                    FabricChannel.elements,
                    FabricChannel.editor,
                    FabricChannel.media,
                  ]}
                >
                  <Switcher />
                </FabricAnalyticsListeners>
              </SwitcherContainer>
            </SwitcherDrawerContainer>
          )}
        </DrawerContainer>
      </Background>
    );
  }

  private closeDrawer = () => {
    this.setState({
      closingDrawer: this.props.drawerStore.currentDrawer,
    });
    this.props.drawerStore.closeDrawer();
  };

  private onBackgroundClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.currentTarget !== e.target) {
      // ignore bubbled clicks
      return;
    }
    this.closeDrawer();
  };

  // Once the drawer has been transitioned off screen, don't render the iframe any more
  private pruneDrawer = (): void => {
    this.setState({
      closingDrawer: DrawerType.None,
    });
  };
}

export default inject(({ drawerStore }: Store) => ({
  drawerStore,
}))(injectIntl(withAnalytics(Drawer)));
